import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Check,
  TrendingUp,
  TrendingDown,
  DollarSign,
  MessageSquare,
} from "lucide-react";
import { DollarBillIcon } from "@/components/DollarBillIcon";

export const HeroSection = () => {
  const navigate = useNavigate();

  return (
    <section className="relative pt-[80px] overflow-hidden bg-stone">
      <div className="container mx-auto max-w-7xl px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center min-h-[calc(100vh-80px)] py-16 lg:py-24">
          {/* Left — Copy */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <div className="space-y-6">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15, duration: 0.6 }}
                className="text-5xl md:text-6xl lg:text-[4.25rem] font-bold text-midnight leading-[1.08] tracking-tight"
              >
                Clarity for
                <br />
                <span className="text-vault-green">Every Dollar</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="text-lg md:text-xl text-slate-brand leading-relaxed max-w-xl"
              >
                What did I make last week? What am I spending on? 
                How much cash do I actually have? Ask Bill. He's 
                already looking into it.
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Button
                size="lg"
                onClick={() => navigate("/contact")}
                className="bg-vault-green hover:bg-vault-green/90 text-white text-base px-8 py-6 rounded-lg shadow-md hover:shadow-lg transition-all"
              >
                Get Started
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-base px-8 py-6 border-2 border-midnight/20 text-midnight hover:bg-midnight hover:text-white transition-all rounded-lg"
                onClick={() => {
                  document
                    .getElementById("how-it-works")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                Learn More
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="flex flex-wrap items-center gap-5 text-sm text-slate-brand"
            >
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-vault-green" />
                <span>Weekly P&L reports</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-vault-green" />
                <span>Plain English answers</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-vault-green" />
                <span>Setup in minutes</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right — Interactive Dashboard Preview */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.7 }}
            className="relative hidden lg:block"
          >
            <div className="relative">
              {/* Main dashboard card */}
              <motion.div
                className="bg-white rounded-2xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.15)] border border-fog/80 overflow-hidden"
                whileHover={{ y: -2 }}
                transition={{ duration: 0.3 }}
              >
                {/* Dashboard header */}
                <div className="px-6 py-4 border-b border-fog flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <DollarBillIcon size={32} />
                    <div>
                      <div className="text-sm font-semibold text-midnight">
                        Weekly P&L
                      </div>
                      <div className="text-[11px] text-slate-brand">
                        Mar 10 – Mar 16, 2026
                      </div>
                    </div>
                  </div>
                  <div className="text-[11px] text-vault-green font-medium bg-vault-green/10 px-2.5 py-1 rounded-full">
                    Updated today
                  </div>
                </div>

                {/* KPI row */}
                <div className="px-6 py-5 grid grid-cols-3 gap-4">
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <div className="text-[11px] text-slate-brand font-medium mb-1">
                      Revenue
                    </div>
                    <div className="text-xl font-bold text-midnight">
                      $12,840
                    </div>
                    <div className="flex items-center gap-1 mt-1">
                      <TrendingUp className="w-3 h-3 text-signal-green" />
                      <span className="text-[10px] font-medium text-signal-green">
                        +8.2%
                      </span>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                  >
                    <div className="text-[11px] text-slate-brand font-medium mb-1">
                      Food Cost
                    </div>
                    <div className="text-xl font-bold text-midnight">28.4%</div>
                    <div className="flex items-center gap-1 mt-1">
                      <TrendingDown className="w-3 h-3 text-signal-red" />
                      <span className="text-[10px] font-medium text-signal-red">
                        +1.2pt
                      </span>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                  >
                    <div className="text-[11px] text-slate-brand font-medium mb-1">
                      Net Profit
                    </div>
                    <div className="text-xl font-bold text-vault-green">
                      $2,340
                    </div>
                    <div className="flex items-center gap-1 mt-1">
                      <TrendingUp className="w-3 h-3 text-signal-green" />
                      <span className="text-[10px] font-medium text-signal-green">
                        +12%
                      </span>
                    </div>
                  </motion.div>
                </div>

                {/* Mini bar chart */}
                <div className="px-6 pb-4">
                  <div className="text-[11px] text-slate-brand font-medium mb-3">
                    Daily Revenue
                  </div>
                  <div className="flex items-end gap-1.5 h-16">
                    {[65, 48, 72, 55, 80, 62, 90].map((h, i) => (
                      <motion.div
                        key={i}
                        initial={{ height: 0 }}
                        animate={{ height: `${h}%` }}
                        transition={{
                          delay: 0.9 + i * 0.06,
                          duration: 0.4,
                          ease: "easeOut",
                        }}
                        className={`flex-1 rounded-sm ${
                          i === 6
                            ? "bg-vault-green"
                            : "bg-vault-green/20"
                        }`}
                      />
                    ))}
                  </div>
                  <div className="flex justify-between mt-1.5">
                    {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
                      <span
                        key={i}
                        className="flex-1 text-center text-[9px] text-slate-brand/60"
                      >
                        {d}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Top items */}
                <div className="px-6 pb-5">
                  <div className="text-[11px] text-slate-brand font-medium mb-2.5">
                    Top Menu Items by Profit
                  </div>
                  <div className="space-y-2">
                    {[
                      { name: "Smash Burger", profit: "$3.80", pct: "42%" },
                      { name: "Loaded Fries", profit: "$2.90", pct: "38%" },
                      { name: "Chicken Wrap", profit: "$1.20", pct: "15%" },
                    ].map((item, i) => (
                      <motion.div
                        key={item.name}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 1.2 + i * 0.08 }}
                        className="flex items-center justify-between text-[12px]"
                      >
                        <span className="text-midnight font-medium">
                          {item.name}
                        </span>
                        <div className="flex items-center gap-3">
                          <span className="text-vault-green font-semibold">
                            {item.profit}
                          </span>
                          <div className="w-16 h-1.5 bg-fog rounded-full overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: item.pct }}
                              transition={{
                                delay: 1.3 + i * 0.1,
                                duration: 0.5,
                              }}
                              className="h-full bg-vault-green rounded-full"
                            />
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Floating Bill chat bubble */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ delay: 1.5, duration: 0.5, type: "spring" }}
                className="absolute -bottom-8 -left-8 bg-white rounded-xl shadow-lg border border-fog p-4 max-w-[240px]"
              >
                <div className="flex items-start gap-2.5">
                  <div className="w-7 h-7 rounded-full bg-vault-green flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-3.5 h-3.5 text-white" />
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold text-midnight mb-0.5">
                      Bill says
                    </div>
                    <p className="text-[11px] text-slate-brand leading-snug">
                      Your chicken cost is up 18% this week. Switch to vendor B
                      to save $240/mo.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Floating revenue badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.3, type: "spring" }}
                className="absolute -top-4 -right-4 bg-midnight text-white rounded-xl shadow-lg px-4 py-3"
              >
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-vault-green" />
                  <div>
                    <div className="text-[10px] text-white/60">
                      This week
                    </div>
                    <div className="text-sm font-bold">+$2,340 profit</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Mobile — simplified dashboard card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="lg:hidden"
          >
            <div className="bg-white rounded-xl shadow-lg border border-fog p-5">
              <div className="flex items-center gap-3 mb-4">
                <DollarBillIcon size={32} />
                <div className="text-sm font-semibold text-midnight">
                  Weekly P&L — Mar 10–16
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <div className="text-[11px] text-slate-brand">Revenue</div>
                  <div className="text-lg font-bold text-midnight">$12.8K</div>
                  <div className="text-[10px] text-signal-green">+8.2%</div>
                </div>
                <div>
                  <div className="text-[11px] text-slate-brand">Food Cost</div>
                  <div className="text-lg font-bold text-midnight">28.4%</div>
                  <div className="text-[10px] text-signal-red">+1.2pt</div>
                </div>
                <div>
                  <div className="text-[11px] text-slate-brand">Profit</div>
                  <div className="text-lg font-bold text-vault-green">
                    $2,340
                  </div>
                  <div className="text-[10px] text-signal-green">+12%</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
